
import { defineStore } from 'pinia'
import asArray from "@/utils/as-array";

export function createDataModelStore(name, service, maxCacheAge = 60000) {
    return defineStore(name, {
        state: () => {
            return {
                cache: {
                    usage: {},
                    queries: {}
                },
                byId: {},
                queryLoading: {},
                errors: {},
            }
        },
        getters: {
            items: (state) => {
                return (queryId) => {
                    if (!state.cache.queries[queryId]) return [];
                    return state.cache.queries[queryId].response.data.map(
                        id => state.byId[id]
                    );
                }
            },
            item: (state) => {
                return (id) => state.byId[id] || null;
            },
            response: (state) => {
                return (queryId) => {
                    if (!state.cache.queries[queryId]) return null;
                    const response = state.cache.queries[queryId].response;
                    const data = response.data;
                    return {
                        ...response,
                        data: Array.isArray(data)
                            ? data.map(id => state.byId[id])
                            : state.byId[data.id]
                    };
                }
            },
            loading: (state) => {
                return (queryId) => state.queryLoading[queryId] || false;
            },
            error: (state) => {
                return (queryId) => state.errors[queryId] || false;
            },
            meta: (state) => {
                return (queryId) => {
                    if (!state.cache.queries[queryId]) return null;
                    return state.cache.queries[queryId].response.meta;
                }
            }
        },
        actions: {
            async fetchItem({ key, query, useCache = true, extra }) {
                if (!key) throw new Error("Missing `key` attribute.");
                const queryId = `ITEM_${JSON.stringify(query)}`;
                // Cleanup the cache.
                this.cleanup();

                this.cache.usage[key] = queryId;
                const cacheHit = useCache && this.byId[queryId];
                //if (cacheHit) console.log("From cache:", queryId);
                if (!cacheHit) {
                    const response = await service.find(query, extra);
                    if (response) {
                        this.addQuery({
                            response,
                            id: queryId,
                            query
                        });
                    } else {
                        throw new Error("Item not found");
                    }
                }

                return queryId;
            },
            fetchItems({ key, query, useCache = true, extra }) {
                if (!key) throw new Error("Missing `key` attribute.");
                const queryId = `ITEMS_${JSON.stringify(query)}`;

                // By saving the `queryId` with the given `key` in the usage table, we can later
                // determine which queries are still active and can not be pruned from cache.
                this.cache.usage[key] = queryId;

                const cacheHit = useCache && this.cache.queries[queryId];
                //if (cacheHit) console.log("From cache:", queryId);
                if (!cacheHit) {
                    this.queryLoading[queryId] = true;
                    service.list(query, extra)
                        .then(response => {
                            if (response) {
                                this.addQuery({
                                    response,
                                    id: queryId,
                                    query
                                });
                            }

                            this.queryLoading[queryId] = false;
                        })
                        .catch(() => {
                            this.queryLoading[queryId] = false;
                            this.errors[queryId] = true;
                        });
                }

                return queryId;
            },
            updateItem({ item }) {
                if (!item) throw new Error("Missing `item` attribute.");
                this.byId[item.id] = item;
            },
            decrementItemProperty({ id, property }) {
                if (!id) throw new Error("Missing `id` attribute.");
                if (!property) throw new Error("Missing `property` attribute.");

                const existingItem = this.byId[id];
                if (!existingItem) {
                    throw new Error("Item not found");
                }
                if (!existingItem[property]) {
                    throw new Error("Property not found");
                }

                if (existingItem[property] <= 0) {
                    return;
                }
                existingItem[property] -= amount;
                this.byId[id] = existingItem;
            },
            incrementItemProperty({ id, property }) {
                if (!id) throw new Error("Missing `id` attribute.");
                if (!property) throw new Error("Missing `property` attribute.");

                const existingItem = this.byId[id];
                if (!existingItem) {
                    throw new Error("Item not found");
                }
                if (!existingItem[property]) {
                    throw new Error("Property not found");
                }

                existingItem[property] += 1;
                this.byId[id] = existingItem;
            },
            patchItem(id, item) {
                if (!item) throw new Error("Missing `item` attribute.");
                const existingItem = this.byId[id];
                if (!existingItem) {
                    throw new Error("Item not found");
                }

                if (
                    existingItem?.related &&
                    Object.keys(existingItem.related).length
                ) {
                    const existingRelated = existingItem.related;
                    const newRelated = item.related;
                    item.related = { ...existingRelated, ...newRelated };
                }

                if (
                    existingItem?.counts &&
                    Object.keys(existingItem.counts).length
                ) {
                    const existingCounts = existingItem.counts;
                    const newCounts = item.counts;
                    item.counts = { ...existingCounts, ...newCounts };
                }

                this.byId[id] = { ...existingItem, ...item };
            },
            prependItem({ key, query, item }) {
                return this.appendItem({ key, query, item }, true);
            },
            appendItem({ key, query, item }, prepend = false) {
                if (!item) throw new Error("Missing `item` attribute.");
                if (!key) throw new Error("Missing `key` attribute.");
                const queryId = `ITEMS_${JSON.stringify(query)}`;

                // By saving the `queryId` with the given `key` in the usage table, we can later
                // determine which queries are still active and can not be pruned from cache.
                this.cache.usage[key] = queryId;
                let response = this.response(queryId);
                if (response) {
                    if (prepend) {
                        response.data.unshift(item);
                    } else {
                        response.data.push(item);
                    }
                } else {
                    response = {
                        data: [item]
                    };
                }

                this.addQuery({ response, id: queryId, query });

                return queryId;
            },
            addResponse({ key, query, useCache = true, response }) {
                if (!response) throw new Error("Missing `response` attribute.");
                    if (!key) throw new Error("Missing `key` attribute.");
                    const queryId = `ITEMS_${JSON.stringify(query)}`;

                    // By saving the `queryId` with the given `key` in the usage table, we can later
                    // determine which queries are still active and can not be pruned from cache.
                    this.cache.usage[key] = queryId;

                    const cacheHit = useCache && this.cache.queries[queryId];
                    //if (cacheHit) console.log("From cache:", queryId);
                    if (!cacheHit) {
                        this.addQuery({ response, id: queryId, query });
                    }

                    return queryId;
            },
            addQuery({response, id, query}) {
                const queryDetails = {
                    createdAt: Date.now(),
                    id,
                    response: {
                        ...response,
                        data: Array.isArray(response.data)
                            ? response.data.map(x => `${x.id}`)
                            : `${response.data.id}`
                    },
                    query
                };
                this.cache.queries[id] = queryDetails;
                asArray(response.data).forEach(item => {
                    if (
                        item.id in this.byId &&
                        this.byId[item.id]?.related &&
                        Object.keys(this.byId[item.id]?.related).length
                    ) {
                        const existingRelated = this.byId[item.id].related;
                        const newRelated = item.related;
                        item.related = { ...existingRelated, ...newRelated };
                    }
                    this.byId[item.id] = item;
                });
            },
            cleanup(force) {
                const queriyIdsInUse = Object.values(this.cache.usage);
                const unusedQueries = Object.values(this.cache.queries).filter(
                    x => !queriyIdsInUse.includes(x.id)
                );
                const cacheAge = force ? 0 : maxCacheAge;
                const expiredQueries = unusedQueries.filter(
                    x => x.createdAt < Date.now() - cacheAge
                );
                // Delete quries from cache if they are expired.
                expiredQueries.forEach(queryDetail => {
                    delete this.cache.queries[queryDetail.id];
                    delete this.queryLoading[queryDetail.id];
                });

                const itemIdsInUse = Object.values(this.cache.queries).reduce(
                    (prev, queryDetail) => [
                        ...prev,
                        ...asArray(queryDetail.response.data)
                    ],
                    []
                );
                const expiredItems = Object.keys(this.byId).filter(
                    x => !itemIdsInUse.includes(x)
                );
                // Delete items which are not referenced anymore in the cache.
                expiredItems.forEach(id => {
                    delete this.byId[id];
                });
            },
            cleanupQuery( queryId) {
                delete this.cache.queries[queryId];
                delete this.queryLoading[queryId];

                const itemIdsInUse = Object.values(this.cache.queries).reduce(
                    (prev, queryDetail) => [
                        ...prev,
                        ...asArray(queryDetail.response.data)
                    ],
                    []
                );
                const expiredItems = Object.keys(this.byId).filter(
                    x => !itemIdsInUse.includes(x)
                );
                // Delete items which are not referenced anymore in the cache.
                expiredItems.forEach(id => {
                    delete this.byId[id];
                });
            },
            removeQueries( {checkFn} ) {
                const contextQueries = Object.values(this.cache.queries).filter(
                    (x) => {
                        let query = x.id.replace(/^(.*?)_/, '');
                        query = JSON.parse(query);
                        if (checkFn(query) ) {
                            return true;
                        }
                        return false;
                    }
                );

                // Delete quries from cache if they are expired.
                contextQueries.forEach(queryDetail => {
                    delete this.cache.queries[queryDetail.id];
                    delete this.queryLoading[queryDetail.id];
                });
            },
            removeItem(id) {
                 //remove item from the caches
                 Object.values(this.cache.queries).forEach(queryDetail => {
                    if (Array.isArray(queryDetail.response.data)){
                        let index = queryDetail.response.data.findIndex(
                            x => parseInt(x, 10) === parseInt(id, 10)
                        );
                        if (index >= 0) {
                            queryDetail.response.data.splice(index, 1);
                        }
                    }else{
                        queryDetail.response.data = [];
                    }
                });
            }
        }
    });
}

export const useDataModelStore = createDataModelStore;
